import { useEffect, useMemo } from 'react';

import dotize from 'dotize';
import cloneDeep from 'lodash-es/cloneDeep';
import merge from 'lodash-es/merge';
import { useDispatch, useSelector } from 'react-redux';

import useBrains from '@/hooks/useBrains';
import useLocalStorage from '@/hooks/useLocalStorage';
import { updateContext } from '@/modules/tryIt/redux/actions';
import { selectContext, selectIsReplay } from '@/modules/tryIt/redux/selectors';
import { selectAccountId, selectBrainId } from '@/redux/session/selectors';

import { useVariablesSection } from './useVariablesSection';
import { StateContextVariable } from '../../types';

type PersistedLockedVariable = Pick<StateContextVariable, 'label' | 'value'>;
type PersistedLockedBrain = { account_id: string; variables: PersistedLockedVariable[] };
export type LockedByBrain = Record<string, PersistedLockedBrain>;

const LOCKED_VARIABLES_STORAGE_KEY = 'tryItLockedContextVariables';
const EMPTY_LOCKED_LIST: PersistedLockedVariable[] = [];

// ============================================================================
// Pure helpers — no React, no Redux. Shape transforms for LockedByBrain.
// ============================================================================

export const upsertVariable = (
  variables: PersistedLockedVariable[],
  label: string,
  value: PersistedLockedVariable['value']
): PersistedLockedVariable[] => [...variables.filter((v) => v.label !== label), { label, value }];

export const removeVariable = (
  variables: PersistedLockedVariable[],
  label: string
): PersistedLockedVariable[] => variables.filter((v) => v.label !== label);

export const writeBrainEntry = (
  store: LockedByBrain,
  brainId: string,
  accountId: string,
  variables: PersistedLockedVariable[]
): LockedByBrain => {
  const next = { ...store };
  if (variables.length === 0) {
    delete next[brainId];
  } else {
    next[brainId] = { account_id: accountId, variables };
  }
  return next;
};

export const syncVariableValues = (
  variables: PersistedLockedVariable[],
  currentByLabel: Map<string, PersistedLockedVariable['value']>
): PersistedLockedVariable[] => {
  let changed = false;
  const next = variables.map((v) => {
    const current = currentByLabel.get(v.label);
    if (current !== undefined && current !== v.value) {
      changed = true;
      return { ...v, value: current };
    }
    return v;
  });
  return changed ? next : variables;
};

export const pruneStaleBrains = (
  store: LockedByBrain,
  accountId: string,
  validBrainIds: Set<string>
): LockedByBrain | null => {
  const next: LockedByBrain = {};
  let changed = false;
  for (const [id, entry] of Object.entries(store)) {
    if (entry.account_id !== accountId || validBrainIds.has(id)) {
      next[id] = entry;
    } else {
      changed = true;
    }
  }
  return changed ? next : null;
};

// ============================================================================
// Hook
// ============================================================================

export const useLockedContextVariables = () => {
  const dispatch = useDispatch();
  const accountId = useSelector(selectAccountId);
  const brainId = useSelector(selectBrainId);
  const storeContext = useSelector(selectContext);
  const isReplay = useSelector(selectIsReplay);

  const { brains } = useBrains();
  const { formattedContextVariables } = useVariablesSection();
  const [lockedByBrain, setLockedByBrain] = useLocalStorage<LockedByBrain>(
    LOCKED_VARIABLES_STORAGE_KEY,
    {}
  );

  // Self-heal: drop entries for deleted brains on the current account; entries
  // from other accounts are left untouched because `useBrains` is account-scoped.
  useEffect(() => {
    if (!brains || !accountId) {
      return;
    }
    const validBrainIds = new Set(brains.map((b) => b.brain_id));
    setLockedByBrain((prev) => pruneStaleBrains(prev, accountId, validBrainIds) ?? prev);
  }, [accountId, brains, setLockedByBrain]);

  const lockedForBrain = useMemo(
    () => (brainId && lockedByBrain[brainId]?.variables) || EMPTY_LOCKED_LIST,
    [brainId, lockedByBrain]
  );

  const stateVariables = useMemo<StateContextVariable[]>(
    () =>
      formattedContextVariables.map((variable) => ({
        ...variable,
        isLocked: lockedForBrain.some((v) => v.label === variable.label),
      })),
    [formattedContextVariables, lockedForBrain]
  );

  // Re-hydrate locked variables into Redux context when the conversation was
  // reset (context collapses to a single entry). Preserves locked values
  // across "Reset conversation" and page reloads.
  useEffect(() => {
    if (isReplay || storeContext.length !== 1 || lockedForBrain.length === 0) {
      return;
    }
    const presentLabels = new Set(formattedContextVariables.map((v) => v.label));
    const missingLocked = lockedForBrain.filter((v) => !presentLabels.has(v.label));
    if (missingLocked.length === 0) {
      return;
    }
    const newContext = cloneDeep(storeContext[0]) ?? {};
    const patch = dotize.backward(Object.fromEntries(missingLocked.map((v) => [v.label, v.value])));
    merge(newContext, patch);
    dispatch(updateContext([newContext]));
  }, [dispatch, formattedContextVariables, isReplay, lockedForBrain, storeContext]);

  // Sync persisted snapshot with Redux while the conversation is active. A
  // brain run can mutate a locked variable's value mid-conversation; without
  // this, "Reset conversation" would re-hydrate the stale lock-time value.
  // Labels absent from Redux are skipped so the snapshot survives the
  // post-reset window before re-hydration runs.
  useEffect(() => {
    if (!brainId || !accountId || lockedForBrain.length === 0) {
      return;
    }
    const currentByLabel = new Map(formattedContextVariables.map((v) => [v.label, v.value]));
    const nextVariables = syncVariableValues(lockedForBrain, currentByLabel);
    if (nextVariables === lockedForBrain) {
      return;
    }
    setLockedByBrain((prev) => writeBrainEntry(prev, brainId, accountId, nextVariables));
  }, [accountId, brainId, formattedContextVariables, lockedForBrain, setLockedByBrain]);

  const toggleLock = (variable: StateContextVariable) => {
    if (!brainId || !accountId) {
      return;
    }
    setLockedByBrain((prev) => {
      const existing = prev[brainId]?.variables ?? [];
      const nextVariables = variable.isLocked
        ? removeVariable(existing, variable.label)
        : upsertVariable(existing, variable.label, variable.value);
      return writeBrainEntry(prev, brainId, accountId, nextVariables);
    });
  };

  return { stateVariables, toggleLock };
};
